/**
 * Attachments API
 */

import { apiClient } from './client';
import { supabase } from '@/lib/supabase';
import type {
  InitAttachmentRequest,
  InitAttachmentResponse,
  FinalizeAttachmentRequest,
  FinalizeAttachmentResponse,
} from './types';

/**
 * Initialize an attachment upload to get a signed URL
 *
 * @param request - Attachment metadata
 * @returns Signed upload URL and attachment ID
 */
export async function initAttachmentUpload(
  request: InitAttachmentRequest
): Promise<InitAttachmentResponse> {
  return apiClient<InitAttachmentResponse>('init-attachment-upload', {
    method: 'POST',
    body: request,
  });
}

/**
 * Upload a file to Supabase Storage using the signed URL token
 *
 * Uses the Supabase client's uploadToSignedUrl method which handles
 * all the CORS and authentication details correctly.
 *
 * @param storagePath - The storage path from initAttachmentUpload
 * @param token - The upload token from initAttachmentUpload
 * @param file - File to upload
 * @param onProgress - Optional progress callback (0-100) - Note: not supported with Supabase client
 * @returns Promise that resolves when upload completes
 */
export async function uploadToSignedUrl(
  storagePath: string,
  token: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  // Signal start of upload
  if (onProgress) {
    onProgress(0);
  }

  const { error } = await supabase.storage
    .from('attachments')
    .uploadToSignedUrl(storagePath, token, file, {
      contentType: file.type,
      upsert: false,
    });

  if (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }

  // Signal completion
  if (onProgress) {
    onProgress(100);
  }
}

/**
 * Finalize an attachment upload after file has been uploaded to storage
 *
 * This confirms the file exists and marks the attachment as ready.
 * Should be called after uploadToSignedUrl completes successfully.
 *
 * @param request - Contains attachment_id to finalize
 * @returns Finalized attachment details
 */
export async function finalizeAttachmentUpload(
  request: FinalizeAttachmentRequest
): Promise<FinalizeAttachmentResponse> {
  return apiClient<FinalizeAttachmentResponse>('finalize-attachment-upload', {
    method: 'POST',
    body: request,
  });
}
